import { db } from '../firebase';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    collection,
    addDoc,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    serverTimestamp,
    Timestamp,
    increment,
    arrayUnion,
    deleteDoc
} from 'firebase/firestore';
import { Message } from '../types/tutor';

export const OSP_FREE_LIMIT = 5; // Testing limit
export const OSP_PRO_LIMIT = 50; // Subscription limit

interface OspUsage {
    count: number;
    lastReset: Timestamp;
    hasUsedTrial?: boolean;
}

interface UserData {
    zsvPro?: boolean;
    role?: string;
    ospAiProActiveUntil?: Timestamp;
    ospUsage?: OspUsage;
    trialAbuseBlocked?: boolean;
    ospSubscriptionType?: 'trial' | 'full' | 'ultimate';
    ospSubscriptionStatus?: string;
}

/**
 * Entitlement status for OSP AI Tutor
 */
export enum OspEntitlementStatus {
    UNKNOWN = 'UNKNOWN',  // Loading state, Firestore data not yet loaded
    PRO_ACTIVE = 'PRO_ACTIVE',
    TRIAL_ELIGIBLE = 'TRIAL_ELIGIBLE',
    TRIAL_NOT_ELIGIBLE = 'TRIAL_NOT_ELIGIBLE'
}

export interface OspEntitlementState {
    status: OspEntitlementStatus;
    isPro: boolean;
    hasUsedTrial: boolean;
    isBlocked: boolean;
    subscriptionType: 'trial' | 'full' | 'ultimate' | null;
    expiryDate: Date | null;
}

/**
 * Single source of truth for OSP entitlement status.
 * Determines if user can see/trigger trial based on Firestore data.
 * @param userDoc - User document data from Firestore (null/undefined = loading)
 * @param now - Current time for Pro expiry check
 */
export const getOspEntitlementStatus = (
    userDoc: UserData | null | undefined,
    now: Date = new Date()
): OspEntitlementState => {
    // UNKNOWN state: Firestore data not loaded yet
    if (userDoc === null || userDoc === undefined) {
        return {
            status: OspEntitlementStatus.UNKNOWN,
            isPro: false,
            hasUsedTrial: false,
            isBlocked: false,
            subscriptionType: null,
            expiryDate: null
        };
    }

    const hasUsedTrial = userDoc.ospUsage?.hasUsedTrial || false;
    const isBlocked = userDoc.trialAbuseBlocked || false;
    const subscriptionType = userDoc.ospSubscriptionType || null;

    const hasExpiredTimestamp = userDoc.ospAiProActiveUntil && userDoc.ospAiProActiveUntil.toDate() <= now;

    // Check for past_due/unpaid lockouts
    const isPastDueOrCanceled =
        userDoc.ospSubscriptionStatus === 'past_due' ||
        userDoc.ospSubscriptionStatus === 'unpaid' ||
        userDoc.ospSubscriptionStatus === 'canceled';

    // Check if Pro is active via ospAiProActiveUntil timestamp
    if (userDoc.ospAiProActiveUntil && !isPastDueOrCanceled) {
        const expiryDate = userDoc.ospAiProActiveUntil.toDate();
        const isPro = expiryDate > now;

        if (isPro) {
            return {
                status: OspEntitlementStatus.PRO_ACTIVE,
                isPro: true,
                hasUsedTrial,
                isBlocked,
                subscriptionType,
                expiryDate
            };
        }
    }

    // Fallback: ospSubscriptionType === 'ultimate' or 'trial' means Premium subscriber
    // BUT only if not expired, and not past due.
    if (!hasExpiredTimestamp && !isPastDueOrCanceled && (userDoc.ospSubscriptionType === 'ultimate' || userDoc.ospSubscriptionType === 'trial')) {
        return {
            status: OspEntitlementStatus.PRO_ACTIVE,
            isPro: true,
            hasUsedTrial,
            isBlocked,
            subscriptionType,
            expiryDate: null
        };
    }

    // Not Pro - check trial eligibility
    if (hasUsedTrial || isBlocked) {
        return {
            status: OspEntitlementStatus.TRIAL_NOT_ELIGIBLE,
            isPro: false,
            hasUsedTrial,
            isBlocked,
            subscriptionType,
            expiryDate: null
        };
    }

    return {
        status: OspEntitlementStatus.TRIAL_ELIGIBLE,
        isPro: false,
        hasUsedTrial: false,
        isBlocked: false,
        subscriptionType,
        expiryDate: null
    };
};

/**
 * Checks if the user has an active OSP AI Pro subscription and if they used the trial.
 * @deprecated Use getOspEntitlementStatus instead for consistent state
 */
export const checkSubscriptionStatus = async (userId: string): Promise<{ isPro: boolean; hasUsedTrial: boolean; isBlocked: boolean; subscriptionType: 'trial' | 'full' | null }> => {
    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) return { isPro: false, hasUsedTrial: false, isBlocked: false, subscriptionType: null };

        const data = userSnap.data() as UserData;
        const hasUsedTrial = data.ospUsage?.hasUsedTrial || false;
        const isBlocked = data.trialAbuseBlocked || false;
        const subscriptionType = (data as any).ospSubscriptionType || null;

        if (!data.ospAiProActiveUntil) return { isPro: false, hasUsedTrial, isBlocked, subscriptionType: null };

        const now = new Date();
        const expiryDate = data.ospAiProActiveUntil.toDate();

        return { isPro: expiryDate > now, hasUsedTrial, isBlocked, subscriptionType };
    } catch (error) {
        console.error("Error checking subscription status", error);
        return { isPro: false, hasUsedTrial: false, isBlocked: false, subscriptionType: null };
    }
};

/**
 * Gets the daily usage count for a user, handling the 24-hour reset logic.
 */
export const getDailyUsage = async (userId: string): Promise<number> => {
    try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            // Create basic user doc if doesn't exist? Or assume auth creates it?
            // For now, assume we just init usage if missing.
            await setDoc(userRef, {
                ospUsage: {
                    count: 0,
                    lastReset: serverTimestamp()
                }
            }, { merge: true });
            return 0;
        }

        const data = userSnap.data() as UserData;
        const usage = data.ospUsage;

        if (!usage) {
            // Initialize usage if missing
            await updateDoc(userRef, {
                ospUsage: {
                    count: 0,
                    lastReset: serverTimestamp()
                }
            });
            return 0;
        }

        // lastReset can be null/undefined if serverTimestamp() hasn't resolved yet
        if (!usage.lastReset || !usage.lastReset.toDate) {
            return usage.count || 0;
        }

        const lastResetDate = usage.lastReset.toDate();
        const now = new Date();
        const hoursSinceReset = (now.getTime() - lastResetDate.getTime()) / (1000 * 60 * 60);

        if (hoursSinceReset >= 24) {
            // Reset usage
            await updateDoc(userRef, {
                "ospUsage.count": 0,
                "ospUsage.lastReset": serverTimestamp()
            });
            return 0;
        }

        return usage.count;
    } catch (error) {
        console.error("Error getting daily usage", error);
        return 0; // Fail safe
    }
};

/**
 * Increments the daily usage count for a user.
 */
export const incrementDailyUsage = async (userId: string): Promise<void> => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            "ospUsage.count": increment(1)
        });
    } catch (error) {
        console.error("Error incrementing usage", error);
    }
};

/**
 * Creates a new chat session.
 */
export const createChat = async (userId: string): Promise<string> => {
    try {
        const chatsRef = collection(db, 'users', userId, 'osp_chats');
        const newChatRef = await addDoc(chatsRef, {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            messages: []
        });
        return newChatRef.id;
    } catch (error) {
        console.error("Error creating chat", error);
        throw error;
    }
};

/**
 * Saves a message to a specific chat.
 */
export const saveMessage = async (userId: string, chatId: string, message: Message): Promise<void> => {
    try {
        const chatRef = doc(db, 'users', userId, 'osp_chats', chatId);

        // We store messages as an array field "messages"
        // Using arrayUnion might be tricky if Message objects are complex or need specific uniqueness.
        // Ideally, for Firestore, subcollections of messages are better for scaling, 
        // but requirements said "messages: Array of message objects".
        // Let's stick to array update for simplicity as per requirements, assuming chat length isn't massive.

        // However, arrayUnion only adds unique elements. If we have duplicate text/timestamps it might de-dupe.
        // It's safer to read-modify-write or use a subcollection. 
        // Given the requirement "messages: Array of message objects", I will use arrayUnion 
        // but ensure the object is unique enough (it has ID and timestamp).

        // Actually, to ensure order and consistency without race conditions on the array, 
        // fetching, appending, and updating is acceptable for low-volume checking. 
        // A better approach for Firestore atomicity with arrays is `arrayUnion`, 
        // but let's verify if `message` has any undefined fields (like internalAnalysis might be optional).
        // Firestore rejects undefined values.

        const messageToSave = {
            ...message,
            text: message.text || "", // Ensure no undefined
            timestamp: Timestamp.fromDate(message.timestamp instanceof Date ? message.timestamp : new Date()),
            // Strip undefined fields
            ...(message.internalAnalysis ? { internalAnalysis: message.internalAnalysis } : {})
        };

        // Use updateDoc with arrayUnion
        await updateDoc(chatRef, {
            messages: arrayUnion(messageToSave),
            updatedAt: serverTimestamp()
        });

    } catch (error) {
        console.error("Error saving message", error);
        throw error;
    }
};

/**
 * Summary of a chat for sidebar display (no full messages).
 */
export interface ChatSummary {
    chatId: string;
    title: string;
    updatedAt: Date;
    createdAt: Date;
}

// Task type keyword detection (mirrors gemini-chat.ts detectTaskType)
const TASK_TITLE_MAP: Record<string, string> = {
    'doplnovani_vet': 'Doplňování do vět',
    'vztahy_slova': 'Vztahy mezi slovy',
    'synonyma_antonyma': 'Synonyma a antonyma',
    'co_neni_v_souladu': 'Co není v souladu',
    'kratke_texty': 'Krátké texty',
    'dlouhe_texty': 'Dlouhé texty',
    'porovnavani_textu': 'Porovnávání textů',
    'grafy_tabulky': 'Grafy a tabulky',
    'porovnavani_hodnot': 'Porovnávání hodnot',
    'dostacujici_tvrzeni': 'Dostačující tvrzení',
    'slovni_ulohy': 'Slovní úlohy',
    'matematizace': 'Matematizace',
    'vynechavky': 'Vynechávky',
    'smyslene_operace': 'Smyšlené operace',
    'funkce': 'Funkce',
    'zebry': 'Zebry',
    'verbal_general': 'Verbální oddíl',
    'analyticky_general': 'Analytický oddíl',
};

const GENERAL_TYPES = new Set(['verbal_general', 'analyticky_general']);

function detectTaskType(message: string): string {
    const text = message.toLowerCase();
    if (/verbální.*oddíl|verbál/i.test(text)) return 'verbal_general';
    if (/analytický.*oddíl|analyt|matematik/i.test(text)) return 'analyticky_general';
    if (/doplň|věty|doplnění do vět/i.test(text)) return 'doplnovani_vet';
    if (/vztahy|slova|analogie/i.test(text)) return 'vztahy_slova';
    if (/synonym|antonym/i.test(text)) return 'synonyma_antonyma';
    if (/není v souladu|odporuje textu/i.test(text)) return 'co_neni_v_souladu';
    if (/krátký text|krátké texty/i.test(text)) return 'kratke_texty';
    if (/text|článek|odstavec|vyplývá/i.test(text)) return 'dlouhe_texty';
    if (/porovnávání textů|dva texty|mezi texty/i.test(text)) return 'porovnavani_textu';
    if (/graf|tabulk|procent|o kolik více/i.test(text)) return 'grafy_tabulky';
    if (/porovn[aá]|vlevo|pravo|která.*větší|hodnot/i.test(text)) return 'porovnavani_hodnot';
    if (/dostačující|tvrzení|pro výpočet stačí/i.test(text)) return 'dostacujici_tvrzeni';
    if (/slovní úloha|kuchař|káva|čaj|pokoj/i.test(text)) return 'slovni_ulohy';
    if (/matematizace|výraz vyplývá/i.test(text)) return 'matematizace';
    if (/vynechávk|vynechané místo|roznásobení/i.test(text)) return 'vynechavky';
    if (/operace|symbol|★|@|⊕|definovan[áý]/i.test(text)) return 'smyslene_operace';
    if (/funkce|f\(x\)|g\(a\)|argument/i.test(text)) return 'funkce';
    if (/zebra|přihrádk|tabulková metoda|kdo bydlí/i.test(text)) return 'zebry';
    return 'general';
}

function detectChatTitle(messages: any[]): string {
    const userMsgs = messages.filter((m: any) => m.sender === 'user').slice(0, 5);
    let bestType = 'general';

    // Scan messages in reverse (most recent = most specific topic)
    for (let i = userMsgs.length - 1; i >= 0; i--) {
        const detected = detectTaskType(userMsgs[i].text || '');
        if (detected !== 'general') {
            // Prefer specific types over general ones
            if (!GENERAL_TYPES.has(detected)) {
                return TASK_TITLE_MAP[detected] || detected;
            }
            if (bestType === 'general') {
                bestType = detected;
            }
        }
    }

    if (bestType !== 'general') return TASK_TITLE_MAP[bestType] || bestType;

    // Fallback: truncated first user message
    const firstUserMsg = userMsgs[0];
    if (firstUserMsg?.text) {
        const text = firstUserMsg.text;
        return text.length > 40 ? text.slice(0, 40) + '...' : text;
    }
    return 'Nový chat';
}

/**
 * Lists all chats for a user, ordered by most recent first.
 */
export const listChats = async (userId: string): Promise<ChatSummary[]> => {
    try {
        const chatsRef = collection(db, 'users', userId, 'osp_chats');
        const q = query(chatsRef, orderBy('updatedAt', 'desc'), where('updatedAt', '!=', null));
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            const messages = data.messages || [];
            return {
                chatId: docSnap.id,
                title: detectChatTitle(messages),
                updatedAt: data.updatedAt?.toDate?.() || new Date(),
                createdAt: data.createdAt?.toDate?.() || new Date(),
            };
        });
    } catch (error) {
        console.error("Error listing chats", error);
        return [];
    }
};

/**
 * Deletes a chat.
 */
export const deleteChat = async (userId: string, chatId: string): Promise<void> => {
    try {
        const chatRef = doc(db, 'users', userId, 'osp_chats', chatId);
        await deleteDoc(chatRef);
    } catch (error) {
        console.error("Error deleting chat", error);
        throw error;
    }
};

/**
 * Retrieves a specific chat by ID.
 */
export const getChatById = async (userId: string, chatId: string): Promise<{ chatId: string, messages: Message[] } | null> => {
    try {
        const chatRef = doc(db, 'users', userId, 'osp_chats', chatId);
        const chatSnap = await getDoc(chatRef);
        if (!chatSnap.exists()) return null;
        const data = chatSnap.data();
        const messages = (data.messages || []).map((m: any) => ({
            ...m,
            timestamp: m.timestamp?.toDate ? m.timestamp.toDate() : new Date(m.timestamp)
        }));
        return { chatId: chatSnap.id, messages };
    } catch (error) {
        console.error("Error getting chat by ID", error);
        return null;
    }
};

/**
 * Retrieves the most recent chat or specific chat.
 */
export const getActiveChat = async (userId: string): Promise<{ chatId: string, messages: Message[] } | null> => {
    try {
        const chatsRef = collection(db, 'users', userId, 'osp_chats');
        const q = query(chatsRef, orderBy('updatedAt', 'desc'), where('updatedAt', '!=', null), limit(1));

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();

        // Convert Timestamps back to Dates
        const messages = (data.messages || []).map((m: any) => ({
            ...m,
            timestamp: m.timestamp?.toDate ? m.timestamp.toDate() : new Date(m.timestamp)
        }));

        return {
            chatId: docSnap.id,
            messages
        };
    } catch (error) {
        console.error("Error getting active chat", error);
        return null;
    }
};
